// Enums for form options
export enum Gender {
  MALE = 'Male',
  FEMALE = 'Female',
  OTHER = 'Other',
}

export enum Pronoun {
  HE = 'He',
  SHE = 'She',
  OTHER = 'Other',
}

export enum MaritalStatus {
  SINGLE = 'Single',
  MARRIED = 'Married',
  OTHER = 'Other',
}

export enum EmploymentStatus {
  FULL_TIME = 'Full-Time',
  PART_TIME = 'Part-Time',
}

export enum TaxIdType {
  TIN = 'TIN',
  SSN = 'SSN',
}

export enum NpiType {
  INDIVIDUAL = 'Individual',
  ORGANIZATION = 'Organization',
}

export enum WorkingHoursEntityType {
  PROVIDER = 'provider',
  OPERATORY = 'operatory',
}

export enum DayOfWeek {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday',
}

export enum Specialty {
  GENERAL = 'General',
  ORTHODONTICS = 'Orthodontics',
  PERIODONTICS = 'Periodontics',
}

export enum ProviderWorkPosition {
  DENTIST = 'Dentist',
  DENTAL_HYGIENIST = 'Dental Hygienist',
  DENTAL_ASSISTANT = 'Dental Assistant',
  DENTAL_THERAPIST = 'Dental Therapist',
  DENTAL_NURSE = 'Dental Nurse',
  DENTAL_TECHNICIAN = 'Dental Technician',
  OFFICE_MANAGER = 'Office Manager',
  RECEPTIONIST = 'Receptionist',
  BILLING_AND_INSURANCE_COORDINATOR = 'Billing and Insurance Coordinator',
  ORTHODONTIC_ASSISTANT = 'Orthodontic Assistant',
  ANESTHESIOLOGIST = 'Anesthesiologist',
  ORAL_SURGEON = 'Oral Surgeon',
  ORAL_AND_MAXILLOFACIAL_SURGEON = 'Oral and Maxillofacial Surgeon',
  ORAL_PATHOLOGIST = 'Oral Pathologist',
  PERIODONTIST = 'Periodontist',
  ENDODONTIST = 'Endodontist',
  ORTHODONTIST = 'Orthodontist',
  PROSTHODONTIST = 'Prosthodontist',
  PEDIATRIC_DENTIST = 'Pediatric Dentist',
}

export const PROVIDER_SEAT_ROLES: readonly ProviderWorkPosition[] = [
  ProviderWorkPosition.DENTIST,
  ProviderWorkPosition.DENTAL_HYGIENIST,
  ProviderWorkPosition.DENTAL_THERAPIST,
  ProviderWorkPosition.ANESTHESIOLOGIST,
  ProviderWorkPosition.ORAL_SURGEON,
  ProviderWorkPosition.ORAL_AND_MAXILLOFACIAL_SURGEON,
  ProviderWorkPosition.ORAL_PATHOLOGIST,
  ProviderWorkPosition.PERIODONTIST,
  ProviderWorkPosition.ENDODONTIST,
  ProviderWorkPosition.ORTHODONTIST,
  ProviderWorkPosition.PROSTHODONTIST,
  ProviderWorkPosition.PEDIATRIC_DENTIST,
]

type MaybeWorkRole = string | undefined

export const isProviderSeatRole = (workRole: MaybeWorkRole): boolean =>
  Boolean(workRole && PROVIDER_SEAT_ROLES.some((r) => r === workRole))

export enum WorkStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  HALF_DAY = 'Half-day',
  ON_LEAVE = 'On-leave',
}

export enum ProviderColorLabel {
  RED = 'Red',
  BLUE = 'Blue',
  TEAL = 'Teal',
  PURPLE = 'Purple',
  GREEN = 'Green',
  BRIGHT_RED = 'Bright Red',
  YELLOW = 'Yellow',
  BRIGHT_BLUE = 'Bright Blue',
  BRIGHT_GREEN = 'Bright Green',
  ORANGE = 'Orange',
  PINK = 'Pink',
  BRIGHT_PURPLE = 'Bright Purple',
  BROWN = 'Brown',
}

export const PROVIDER_COLORS = [
  {
    header: '#d8352a',
    background: '#ffcbc8',
    text_color: '#470c08',
    border_color: 'var(--red-400)',
    label: ProviderColorLabel.RED,
  },
  {
    header: '#185dd8',
    background: '#b1d5fb',
    text_color: '#112850',
    border_color: 'var(--blue-400)',
    label: ProviderColorLabel.BLUE,
  },
  {
    header: '#24a1a3',
    background: '#c6f0ed',
    text_color: '#133539',
    border_color: 'var(--teal-400)',
    label: ProviderColorLabel.TEAL,
  },
  {
    header: '#5f21ca',
    background: '#e5dcff',
    text_color: '#270f38',
    border_color: 'var(--violet-400)',
    label: ProviderColorLabel.PURPLE,
  },
  {
    header: '#5d9310',
    background: '#e4f0d0',
    text_color: '#19250e',
    border_color: 'var(--pale-green-400)',
    label: ProviderColorLabel.GREEN,
  },
  {
    header: '#ff0000',
    background: '#ffcccc',
    text_color: '#660000',
    border_color: 'var(--red-400)',
    label: ProviderColorLabel.BRIGHT_RED,
  },
  {
    header: '#ffff00',
    background: '#ffffcc',
    text_color: '#666600',
    border_color: 'var(--yellow-400)',
    label: ProviderColorLabel.YELLOW,
  },
  {
    header: '#0000ff',
    background: '#ccccff',
    text_color: '#000066',
    border_color: 'var(--blue-400)',
    label: ProviderColorLabel.BRIGHT_BLUE,
  },
  {
    header: '#008000',
    background: '#ccffcc',
    text_color: '#003300',
    border_color: 'var(--vivid-green-400)',
    label: ProviderColorLabel.BRIGHT_GREEN,
  },
  {
    header: '#ffa500',
    background: '#ffe6cc',
    text_color: '#663300',
    border_color: 'var(--orange-400)',
    label: ProviderColorLabel.ORANGE,
  },
  {
    header: '#ffc0cb',
    background: '#ffe6f0',
    text_color: '#660033',
    border_color: 'var(--red-400)', // Pink uses red-400 as fallback
    label: ProviderColorLabel.PINK,
  },
  {
    header: '#800080',
    background: '#e6ccff',
    text_color: '#330066',
    border_color: 'var(--violet-400)',
    label: ProviderColorLabel.BRIGHT_PURPLE,
  },
  {
    header: '#a52a2a',
    background: '#ffcccc',
    text_color: '#4d1a1a',
    border_color: 'var(--orange-400)', // Brown uses orange-400 as fallback
    label: ProviderColorLabel.BROWN,
  },
]

/**
 * Gets the border color CSS variable for a provider color by label
 * @param label - The provider color label (e.g., 'Red', 'Blue', etc.)
 * @returns The CSS variable string for the border color, or gray-400 as fallback
 */
export const getProviderBorderColor = (label?: string): string => {
  if (!label) return 'var(--gray-400)'

  const color = PROVIDER_COLORS.find((c) => c.label === label)
  return color?.border_color || 'var(--gray-400)'
}

/**
 * Gets the background color CSS variable (-200 variant) for a provider color by label
 * @param label - The provider color label (e.g., 'Red', 'Blue', etc.)
 * @returns The CSS variable string for the background color, or gray-200 as fallback
 */
export const getProviderBackgroundColor = (label?: string): string => {
  if (!label) return 'var(--gray-200)'

  // Map provider color labels to their corresponding --color-200 CSS variables
  const colorMap: { [key: string]: string } = {
    [ProviderColorLabel.RED]: 'var(--red-200)',
    [ProviderColorLabel.BLUE]: 'var(--blue-200)',
    [ProviderColorLabel.TEAL]: 'var(--teal-200)',
    [ProviderColorLabel.PURPLE]: 'var(--violet-200)',
    [ProviderColorLabel.GREEN]: 'var(--pale-green-200)',
    [ProviderColorLabel.BRIGHT_RED]: 'var(--red-200)',
    [ProviderColorLabel.YELLOW]: 'var(--yellow-200)',
    [ProviderColorLabel.BRIGHT_BLUE]: 'var(--blue-200)',
    [ProviderColorLabel.BRIGHT_GREEN]: 'var(--vivid-green-200)',
    [ProviderColorLabel.ORANGE]: 'var(--orange-200)',
    [ProviderColorLabel.PINK]: 'var(--red-200)', // Pink uses red-200 as fallback
    [ProviderColorLabel.BRIGHT_PURPLE]: 'var(--violet-200)',
    [ProviderColorLabel.BROWN]: 'var(--orange-200)', // Brown uses orange-200 as fallback
  }

  return colorMap[label] || 'var(--gray-200)'
}

/**
 * Gets the text color CSS variable (-700 variant) for a provider color by label
 * @param label - The provider color label (e.g., 'Red', 'Blue', etc.)
 * @returns The CSS variable string for the text color, or gray-700 as fallback
 */
export const getProviderTextColor = (label?: string): string => {
  if (!label) return 'var(--gray-700)'

  // Map provider color labels to their corresponding --color-700 CSS variables
  const colorMap: { [key: string]: string } = {
    [ProviderColorLabel.RED]: 'var(--red-700)',
    [ProviderColorLabel.BLUE]: 'var(--blue-700)',
    [ProviderColorLabel.TEAL]: 'var(--teal-700)',
    [ProviderColorLabel.PURPLE]: 'var(--violet-700)',
    [ProviderColorLabel.GREEN]: 'var(--pale-green-700)',
    [ProviderColorLabel.BRIGHT_RED]: 'var(--red-700)',
    [ProviderColorLabel.YELLOW]: 'var(--yellow-700)',
    [ProviderColorLabel.BRIGHT_BLUE]: 'var(--blue-700)',
    [ProviderColorLabel.BRIGHT_GREEN]: 'var(--vivid-green-700)',
    [ProviderColorLabel.ORANGE]: 'var(--orange-700)',
    [ProviderColorLabel.PINK]: 'var(--red-700)', // Pink uses red-700 as fallback
    [ProviderColorLabel.BRIGHT_PURPLE]: 'var(--violet-700)',
    [ProviderColorLabel.BROWN]: 'var(--orange-700)', // Brown uses orange-700 as fallback
  }

  return colorMap[label] || 'var(--gray-700)'
}

export type USStateOption = { value: string; label: string; zip: string }

export const US_STATE_OPTIONS: USStateOption[] = [
  { value: 'AL', label: 'Alabama (AL)', zip: '35004' },
  { value: 'AK', label: 'Alaska (AK)', zip: '99501' },
  { value: 'AZ', label: 'Arizona (AZ)', zip: '85001' },
  { value: 'AR', label: 'Arkansas (AR)', zip: '72201' },
  { value: 'CA', label: 'California (CA)', zip: '90001' },
  { value: 'CO', label: 'Colorado (CO)', zip: '80201' },
  { value: 'CT', label: 'Connecticut (CT)', zip: '06101' },
  { value: 'DE', label: 'Delaware (DE)', zip: '19901' },
  { value: 'FL', label: 'Florida (FL)', zip: '33101' },
  { value: 'GA', label: 'Georgia (GA)', zip: '30301' },
  { value: 'HI', label: 'Hawaii (HI)', zip: '96801' },
  { value: 'ID', label: 'Idaho (ID)', zip: '83701' },
  { value: 'IL', label: 'Illinois (IL)', zip: '60601' },
  { value: 'IN', label: 'Indiana (IN)', zip: '46201' },
  { value: 'IA', label: 'Iowa (IA)', zip: '50301' },
  { value: 'KS', label: 'Kansas (KS)', zip: '66101' },
  { value: 'KY', label: 'Kentucky (KY)', zip: '40201' },
  { value: 'LA', label: 'Louisiana (LA)', zip: '70112' },
  { value: 'ME', label: 'Maine (ME)', zip: '04101' },
  { value: 'MD', label: 'Maryland (MD)', zip: '21201' },
  { value: 'MA', label: 'Massachusetts (MA)', zip: '02101' },
  { value: 'MI', label: 'Michigan (MI)', zip: '48201' },
  { value: 'MN', label: 'Minnesota (MN)', zip: '55101' },
  { value: 'MS', label: 'Mississippi (MS)', zip: '39201' },
  { value: 'MO', label: 'Missouri (MO)', zip: '63101' },
  { value: 'MT', label: 'Montana (MT)', zip: '59001' },
  { value: 'NE', label: 'Nebraska (NE)', zip: '68101' },
  { value: 'NV', label: 'Nevada (NV)', zip: '89501' },
  { value: 'NH', label: 'New Hampshire (NH)', zip: '03301' },
  { value: 'NJ', label: 'New Jersey (NJ)', zip: '07001' },
  { value: 'NM', label: 'New Mexico (NM)', zip: '87501' },
  { value: 'NY', label: 'New York (NY)', zip: '10001' },
  { value: 'NC', label: 'North Carolina (NC)', zip: '27501' },
  { value: 'ND', label: 'North Dakota (ND)', zip: '58102' },
  { value: 'OH', label: 'Ohio (OH)', zip: '44101' },
  { value: 'OK', label: 'Oklahoma (OK)', zip: '73101' },
  { value: 'OR', label: 'Oregon (OR)', zip: '97201' },
  { value: 'PA', label: 'Pennsylvania (PA)', zip: '19101' },
  { value: 'RI', label: 'Rhode Island (RI)', zip: '02901' },
  { value: 'SC', label: 'South Carolina (SC)', zip: '29201' },
  { value: 'SD', label: 'South Dakota (SD)', zip: '57101' },
  { value: 'TN', label: 'Tennessee (TN)', zip: '37201' },
  { value: 'TX', label: 'Texas (TX)', zip: '73301' },
  { value: 'UT', label: 'Utah (UT)', zip: '84101' },
  { value: 'VT', label: 'Vermont (VT)', zip: '05601' },
  { value: 'VA', label: 'Virginia (VA)', zip: '23218' },
  { value: 'WA', label: 'Washington (WA)', zip: '98001' },
  { value: 'WV', label: 'West Virginia (WV)', zip: '25301' },
  { value: 'WI', label: 'Wisconsin (WI)', zip: '53201' },
  { value: 'WY', label: 'Wyoming (WY)', zip: '82001' },
]

export interface TimeOption {
  value: string
  label: string
}

export const TIME_OPTIONS: TimeOption[] = [
  { value: '06:00:00', label: '6:00 AM' },
  { value: '06:30:00', label: '6:30 AM' },
  { value: '07:00:00', label: '7:00 AM' },
  { value: '07:30:00', label: '7:30 AM' },
  { value: '08:00:00', label: '8:00 AM' },
  { value: '08:30:00', label: '8:30 AM' },
  { value: '09:00:00', label: '9:00 AM' },
  { value: '09:30:00', label: '9:30 AM' },
  { value: '10:00:00', label: '10:00 AM' },
  { value: '10:30:00', label: '10:30 AM' },
  { value: '11:00:00', label: '11:00 AM' },
  { value: '11:30:00', label: '11:30 AM' },
  { value: '12:00:00', label: '12:00 PM' },
  { value: '12:30:00', label: '12:30 PM' },
  { value: '13:00:00', label: '1:00 PM' },
  { value: '13:30:00', label: '1:30 PM' },
  { value: '14:00:00', label: '2:00 PM' },
  { value: '14:30:00', label: '2:30 PM' },
  { value: '15:00:00', label: '3:00 PM' },
  { value: '15:30:00', label: '3:30 PM' },
  { value: '16:00:00', label: '4:00 PM' },
  { value: '16:30:00', label: '4:30 PM' },
  { value: '17:00:00', label: '5:00 PM' },
  { value: '17:30:00', label: '5:30 PM' },
  { value: '18:00:00', label: '6:00 PM' },
  { value: '18:30:00', label: '6:30 PM' },
  { value: '19:00:00', label: '7:00 PM' },
  { value: '19:30:00', label: '7:30 PM' },
  { value: '20:00:00', label: '8:00 PM' },
  { value: '20:30:00', label: '8:30 PM' },
  { value: '21:00:00', label: '9:00 PM' },
]

export const PROVIDER_CALL_OPTIONS = [
  { label: 'Cell Phone', value: 'cellphone' },
  { label: 'Home Number', value: 'home_number' },
  { label: 'Work Contact', value: 'work_contact' },
]
